/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { AdminPageHeader } from '../../Admin/Common/AdminPagesHeader';
import {
    HIRING_CLIENT,
    HIRING_CLIENT_COLUMNS,
    LOCAL_STORAGE,
    QUERY_KEYS,
} from '../../../constants';
import { getHiringClients, getLocalStorageItem, getPlatformById, popup } from '../../../utils';
import { usePagination } from '../../../hooks';
import { Table } from '../../shared';
import { OptionIcon } from '../../../assets/svg/OptionIcon';
import UnhideHiringClientModal from '../../shared/Modal/UnhideHiringClientModal';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';
import { sortBy } from 'lodash';

export const HiringClientListing = () => {
    const { state, pathname } = useLocation();
    const [, setSearchParams] = useSearchParams();
    const isUserPrimary = useSelector((s) => s.accountArray?.isUserPrimary);
    const selectedAccount = useSelector((s) => s.accountArray?.selectedAccount);

    const [response, setResponse] = useState({ data: [], count: 0 });
    const [search, setSearch] = useState('');
    const [selectedPt, setSelectedPt] = useState(undefined);
    const [showHCModal, setShowHCModal] = useState(false);
    const [landingPage, setLandingPage] = useState(false);

    const accountId = getLocalStorageItem(LOCAL_STORAGE.CUSTOMER_ID);
    const pagination = usePagination(response.data.length, response.count);

    const platformsQuery = useQuery({
        queryKey: [QUERY_KEYS.PLATFORMS, selectedAccount],
        queryFn: async () => await getPlatformById({ accountId: selectedAccount }),
        select: (data) => {
            const newPlatforms = [{ id: '', name: 'All Platforms' }, ...sortBy(data.data, 'name')];
            return newPlatforms;
        },
        placeholderData: [{ id: '', name: 'All Platforms' }],
        enabled: !!selectedAccount,
    });

    const filterConfig = [
        {
            id: 'platformFilter',
            name: 'Platform',
            selectedId: selectedPt,
            handleFunction: (_v,id) => {
                setSelectedPt(id);
            },
            options: platformsQuery.data,
        },
    ];

    const [debounceSearch] = useDebouncedValue(search, 500);
    const { data, isFetching, isFetched, isError, error } = useQuery({
        queryKey: [
            QUERY_KEYS.HIRING_CLIENTS,
            accountId,
            selectedPt,
            debounceSearch,
            pagination.offset,
            pagination.limit,
        ],
        queryFn: async () =>
            await getHiringClients({
                account_id: accountId,
                platform_id: selectedPt,
                offset: pagination.offset,
                limit: pagination.limit,
                search: debounceSearch,
            }),
        select: (data) => ({ data: data.data, count: data.count }),
        placeholderData: { data: [], count: 0 },
        enabled: !!accountId,
    });

    useEffect(() => {
        if (data.data && isFetched) {
            setResponse({ data: data.data, count: data.count });
        }
    }, [data, isFetched]);

    if (isError) popup('error', error.message);

    useEffect(() => {
        if (state?.hiringClientName) {
            setSearchParams({ search: state?.hiringClientName });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    return (
        <>
            <AdminPageHeader
                heading={HIRING_CLIENT.PAGE_HEADING}
                buttonText={HIRING_CLIENT.BUTTON_TEXT}
                searchBarPlaceholder={HIRING_CLIENT.SEARCH_BAR_PLACEHOLDER}
                setSearch={setSearch}
                count={pagination.dataCount}
                setCurrentPage={pagination.setCurrentPage}
                backbutton
                filterConfig={filterConfig}
            />
            <Table
                columns={HIRING_CLIENT_COLUMNS}
                data={response.data}
                pagination={pagination}
                pageName={HIRING_CLIENT.PAGE_HEADING}
                name={'hhc'}
                stickyColumns={['Hiring Client']}
                isLoading={isFetching}
            />
            {showHCModal && (
                <UnhideHiringClientModal
                    showModal={showHCModal}
                    setShowModal={setShowHCModal}
                    accountId={selectedAccount}
                />
            )}
            {isUserPrimary && pathname === '/customer/hiring_clients' && (
                <span
                    id='action-btn'
                    title='Unhide Hiring Clients'
                    aria-label='Unhide Hiring Clients'
                    onClick={() => setShowHCModal(true)}
                    className={`unhide-hc-btn ${response?.data?.length === 0 ? 'no-data' : ''}`}
                >
                    <OptionIcon />
                </span>
            )}
        </>
    );
};
